import { HttpClient } from '@angular/common/http';
import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { DialogStatusComponent } from 'src/app/dialog/dialog-status/dialog-status.component';
import { ComponentCanDeactivate } from 'src/app/_guard/pending-changes.guard';
import { AppService, GRSSResponse, GRSSUserRoles } from 'src/app/_services/app.service';
import { AddHolidayComponent } from './add-holiday/add-holiday.component';

@Component({
    selector: 'app-manage-calendar',
    templateUrl: './manage-calendar.component.html',
    styleUrls: ['./manage-calendar.component.css'],
    standalone: false
})
export class ManageCalendarComponent implements OnInit, ComponentCanDeactivate {

  displayColumns: string[] = ['actualDate', 'statutoryDate', 'description', 'exceptions', 'actions'];
  dataSource;
  searchKey: string;
  holidaysLength = 0;
  
  calendarHolidays: Holiday[];
  calendarHolidaysTemp: Holiday[];
  hasStatDays: Boolean = false;

  isLoading: boolean = false;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(private translate: TranslateService, @Inject('BASE_URL') private baseUrl: string, private http: HttpClient, private dialog: MatDialog, public service: AppService) {
    this.service.SetTranslation('administration/manage-calendar');
   }

  ngOnInit() {
    this.GetHolidays();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    let hasChanges: boolean = false;

    if (JSON.stringify(this.calendarHolidays) !== JSON.stringify(this.calendarHolidaysTemp)) {
      hasChanges = true;
    }

    return !hasChanges;
  }

  /** Gets all the holidays */
  GetHolidays() {
    this.isLoading = true;
    this.http.get<Holiday[]>(this.baseUrl + 'api/calendar/getHolidays/manage').subscribe(result => {
      this.calendarHolidays = result;
      this.calendarHolidaysTemp = JSON.parse(JSON.stringify(result));
      this.UpdateCalendarTable();
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
      if (this.service.currentUser.userRole === GRSSUserRoles.Compliance) {
        if (this.displayColumns.includes('actions')) {
          const index = this.displayColumns.indexOf('actions');
          this.displayColumns.splice(index, 1);
        }
      }

      this.calendarHolidays.forEach(hol => {
        if (hol.statutoryDate && this.hasStatDays === false) {
          this.hasStatDays = true;
        }
      });

      if (this.hasStatDays === false && this.displayColumns.includes('statutoryDate')) {
        this.displayColumns.splice(this.displayColumns.indexOf('statutoryDate'), 1);
      }
    }, error => {
      console.error(error);
    }, () => {
      this.isLoading = false;
    });
  }

  /** applying filter for dataSources */
  ApplyFilter() {
    this.dataSource.filter = this.searchKey.trim().toLowerCase();
  }

  /** clear and close search by calling applyfilter */
  OnSearchClear() {
    this.searchKey = '';
    this.ApplyFilter();
  }

  OnCreateHoliday() {
    this.service.isUpdateHoliday = false;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.position = { top: '5%' };
    this.dialog.open(AddHolidayComponent, dialogConfig).afterClosed().subscribe((res: Holiday) => {
      if (res !== null) {
        this.calendarHolidays.push(res);
        this.UpdateCalendarTable();
      }
    });
  }

  /**
   *  Edit icon event of a holiday
   * @param holiday Selected holiday
   */
  OnEditHoliday(holiday: Holiday) {
    this.service.isUpdateHoliday = true;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.position = { top: '5%' };
    dialogConfig.data = {
      holiday: holiday,
      hasStatDays: this.hasStatDays
    };
    this.dialog.open(AddHolidayComponent, dialogConfig).afterClosed().subscribe((res: Holiday) => {
      if (res !== null) {
        const index = this.calendarHolidays.indexOf(holiday, 0);
        this.calendarHolidays.splice(index, 1, res);
        // this.calendarHolidays.push(res);
        this.UpdateCalendarTable();
      }
    });
  }

  /**
   *  Delete icon event of a holiday
   * @param holiday Selected holiday
   */
  OnDeleteHoliday(holiday: Holiday) {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      disableClose: true,
      data: {
        title: this.translate.instant('Calendar.Messages.msgHeaderDelete'),
        message: this.translate.instant('Calendar.Messages.msgParaDelete')
      },
      position: { top: '5%' }
    });
    dialogRef.afterClosed().subscribe(result => {
      // Evaluate the users response.
      if (result === true) {
        const index = this.calendarHolidays.indexOf(holiday, 0);
        this.calendarHolidays.splice(index, 1);
        this.UpdateCalendarTable();
      }
    });
  }

  /** Save button event */
  OnSaveHoliday() {
    this.service.showSpinner();
    this.http.post<GRSSResponse>(this.baseUrl + 'api/calendar/updateHolidays', this.calendarHolidays).subscribe(result => {
      let navigated = false;
      if (result.statusCode === 1) {
        this.GetHolidays();
      } else if (result.statusCode === 4) {
        navigated = true;
        this.service.SaveStatus(true, 'Calendar.Messages.msgHeaderUpdate', result.statusMessage);
      }

      if (!navigated) {
        this.service.SaveStatus(false, 'Calendar.Messages.msgHeaderUpdate', result.statusMessage);
      }
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
    });
  }

  /** Updating the calendar table */
  UpdateCalendarTable() {
    this.holidaysLength = this.calendarHolidays.length;
    this.dataSource = new MatTableDataSource(this.calendarHolidays);
  }

}

/** An interface to access holiday details */
interface Holiday {
  holidayId: string;
  description: string;
  actualDate: string;
  statutoryDate: string;
  exceptions: string[];
}
